import React, { useState, useEffect } from 'react';
import Toast from './Toast';

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    quote: '',
    attribution: '',
    title: '',
    company: '',
    status: 'draft',
    linkedProject: ''
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await fetch('/api/testimonials');
      const data = await response.json();
      setTestimonials(data);
    } catch (error) {
      showToast('Failed to fetch testimonials', 'error');
      console.error('Failed to fetch testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (msg, type = 'default') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `/api/testimonials/${editingId}` : '/api/testimonials';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save testimonial');

      showToast(editingId ? 'Testimonial updated' : 'Testimonial created');
      resetForm();
      fetchTestimonials();
    } catch (error) {
      showToast('Error saving testimonial', 'error');
      console.error('Error saving testimonial:', error);
    }
  };

  const handleEdit = (testi) => {
    setFormData({
      quote: testi.quote,
      attribution: testi.attribution,
      title: testi.title,
      company: testi.company,
      status: testi.status,
      linkedProject: testi.linkedProject || ''
    });
    setEditingId(testi._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      const response = await fetch(`/api/testimonials/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete');
      showToast('Testimonial deleted');
      fetchTestimonials();
    } catch (error) {
      showToast('Error deleting testimonial', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      quote: '',
      attribution: '',
      title: '',
      company: '',
      status: 'draft',
      linkedProject: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const stats = {
    total: testimonials.length,
    featured: testimonials.filter(t => t.status === 'featured').length,
    pending: testimonials.filter(t => t.status === 'draft').length
  };

  if (loading) return <div className="admin-page">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-page__head">
        <div>
          <h1 className="admin-page__title">Testimonials <em>· featured</em></h1>
          <div className="admin-page__sub">
            <span><strong>{stats.total}</strong> total</span>
            <span><strong>{stats.featured}</strong> featured</span>
            <span><strong>{stats.pending}</strong> pending</span>
          </div>
        </div>
        <div className="admin-page__actions">
          <button className="btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>+ New testimonial</button>
        </div>
      </div>

      {showForm && (
        <div style={{ border: '1px solid rgba(244,241,234,0.08)', borderRadius: '2px', padding: '24px', marginTop: '32px', marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '16px', color: 'var(--ivory)' }}>{editingId ? 'Edit Testimonial' : 'New Testimonial'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Quote</label>
              <textarea
                name="quote"
                value={formData.quote}
                onChange={handleInputChange}
                placeholder="Enter the testimonial quote"
                required
                style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', fontFamily: 'monospace', minHeight: '100px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Name</label>
                <input
                  type="text"
                  name="attribution"
                  value={formData.attribution}
                  onChange={handleInputChange}
                  placeholder="Person's name"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., CTO"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Company name"
                  required
                  style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', boxSizing: 'border-box' }}
                >
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  <option value="featured">Featured</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: 'var(--graphite-2)', marginBottom: '6px' }}>Linked Project (optional)</label>
              <input
                type="text"
                name="linkedProject"
                value={formData.linkedProject}
                onChange={handleInputChange}
                placeholder="Project ID"
                style={{ width: '100%', padding: '10px', borderRadius: '2px', border: '1px solid rgba(244,241,234,0.08)', backgroundColor: 'rgba(0,0,0,0.3)', color: 'var(--ivory)', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn-primary">Save</button>
              <button type="button" onClick={resetForm} style={{ padding: '10px 16px', backgroundColor: 'rgba(244,241,234,0.1)', color: 'var(--ivory)', border: 'none', borderRadius: '2px', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginTop: '32px' }}>
        {testimonials.map(testi => (
          <div key={testi._id} style={{ border: '1px solid rgba(244,241,234,0.08)', borderRadius: '2px', padding: '24px' }}>
            <blockquote style={{ fontFamily: 'Instrument Serif', fontSize: '22px', lineHeight: 1.4, marginBottom: '16px', color: 'var(--ivory)', minHeight: '120px' }}>
              "{testi.quote}"
            </blockquote>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingBottom: '16px', borderBottom: '1px solid rgba(244,241,234,0.08)' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'var(--ivory)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '12px' }}>
                {testi.attribution.substring(0, 2).toUpperCase()}
              </div>
              <div>
                <strong style={{ display: 'block', fontSize: '13px' }}>{testi.attribution}</strong>
                <span style={{ fontSize: '11px', color: 'var(--graphite-2)' }}>{testi.title}, {testi.company}</span>
              </div>
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', padding: '2px 6px', backgroundColor: testi.status === 'featured' ? 'rgba(111,207,151,0.2)' : 'rgba(200,180,160,0.2)', color: testi.status === 'featured' ? '#6FCF97' : 'var(--graphite-2)', borderRadius: '2px' }}>
                {testi.status} {testi.status === 'featured' && '· homepage'}
              </span>
              <div style={{ display: 'flex', gap: '12px' }}>
                <span style={{ cursor: 'pointer', color: 'var(--graphite-2)', fontSize: '13px', textDecoration: 'underline' }} onClick={() => handleEdit(testi)}>edit</span>
                <span style={{ cursor: 'pointer', color: '#E07856', fontSize: '13px', textDecoration: 'underline' }} onClick={() => handleDelete(testi._id)}>delete</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {testimonials.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--graphite-2)' }}>
          No testimonials yet. Create your first one!
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} />}
    </div>
  );
}
